import React from "react";
import { ChevronDown } from "lucide-react";

const faqCopy = {
  he: {
    title: "שאלות נפוצות",
    items: [
      { q: "באיזו תדירות מתעדכן מזג האוויר הימי?", a: "נתוני מזג האוויר הימי מתרעננים באופן קבוע, כאשר נתוני התחזית המרכזיים מתעדכנים בערך כל 10 דקות כשהמידע זמין מהספק." },
      { q: "איזו מהירות רוח בטוחה לשייט?", a: "תנאי שייט נוחים תלויים בסירה, בצוות ובמסלול, אך רוחות חזקות, משבים גבוהים או תחזית שמשתנה במהירות הם סימן לשקול דחייה או קיצור הפלגה." },
      { q: "למה גם גובה הגלים וגם הרוח חשובים?", a: "הרוח מראה את הכוח שפועל על הסירה, וגובה הגלים מצביע על נוחות הים וקושי השליטה. גם רוח בינונית יכולה להרגיש מסוכנת אם הגלים תלולים או מבולבלים." },
      { q: "האם אפשר להשתמש בתחזית להחלטות ניווט רשמיות?", a: "השתמשו ב-Marstick ככלי תכנון, אך תמיד אשרו מול תחזיות ימיות רשמיות, הודעות מקומיות, עדכוני VHF, צוות המרינה והתצפיות שלכם לפני יציאה." },
      { q: "למה דף מזג האוויר מבקש מיקום?", a: "גישה למיקום מאפשרת ל-Marstick להציג מזג אוויר ימי, גלים, גאות ושפל והתראות קרובות. אם לא תאשרו מיקום, יוצג מיקום ברירת מחדל." },
      { q: "מה לבדוק לפני יציאה מהמרינה?", a: "בדקו מגמת רוח, משבים, גובה גלים, ראות, זמני גאות ושפל, אזהרות, נמלים חלופיים, דלק, מוכנות צוות והאם התנאים צפויים להשתפר או להחמיר במהלך ההפלגה." },
    ],
  },
  default: {
    title: "Frequently asked questions",
    items: [
      { q: "How often is the marine weather data updated?", a: "Marine weather data is refreshed regularly, with core forecast data updated about every 10 minutes when available from the provider." },
      { q: "What wind speed is safe for sailing?", a: "Comfortable sailing conditions depend on your boat, crew, and route, but many recreational sailors treat stronger winds, high gusts, or rapidly changing forecasts as a reason to delay or shorten a trip." },
      { q: "Why are wave height and wind both important?", a: "Wind shows how much force is acting on the boat, while wave height shows sea comfort and handling difficulty. A moderate wind can still feel unsafe if waves are steep or confused." },
      { q: "Can I use this forecast for official navigation decisions?", a: "Use Marstick as a planning aid, but always confirm with official marine forecasts, local notices, VHF updates, marina staff, and your own observations before departure." },
      { q: "Why does the weather page ask for my location?", a: "Location access lets Marstick show nearby marine weather, waves, tides, and alerts. If you do not allow location access, the page uses a default location instead." },
      { q: "What should I check before leaving the marina?", a: "Check wind trend, gusts, wave height, visibility, tide timing, warnings, alternate ports, fuel, crew readiness, and whether conditions are expected to improve or worsen during the trip." },
    ],
  },
};

export default function WeatherFAQSection({ lang = "en" }) {
  const copy = faqCopy[lang] || faqCopy.default;
  const [openFaqIndex, setOpenFaqIndex] = React.useState(-1);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: copy.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <section className="rounded-[2rem] border border-border bg-muted/35 p-6 md:p-10">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <h2 className="font-heading text-3xl font-black text-foreground">{copy.title}</h2>
      <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
        {copy.items.map((item, index) => {
          const isOpen = openFaqIndex === index;
          return (
            <div key={item.q} className="border-b border-border last:border-b-0">
              <button
                type="button"
                onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-muted/45 md:px-6"
              >
                <span className="font-black text-foreground">{item.q}</span>
                <ChevronDown className={`h-5 w-5 shrink-0 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-border bg-background/70 px-5 py-5 md:px-6">
                  <p className="max-w-3xl leading-8 text-muted-foreground">{item.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}